import { useEffect, useRef, useState } from 'react'
import { ImageIcon, Loader2, UploadCloud } from 'lucide-react'
import { toast } from 'sonner'
import {
  apiError,
  useUpdateWhitelabelMutation,
  useUploadWhitelabelAssetMutation,
  useWhitelabelQuery,
} from '@/store/api'
import type { WhitelabelSettings } from '@shared/types'
import { Button } from '@/components/ui/button'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { ErrorState } from '@/components/ui/empty-state'

const EMPTY: WhitelabelSettings = {
  logo: null,
  favicon: null,
  color: null,
  tutorialVideo: null,
  poweredBy: false,
  poweredLink: null,
  senderName: null,
  replyTo: null,
}

/**
 * Edit a partner's white-label branding, including the sender name and reply-to on the
 * emails SMB owners receive from the product. Shown on the partner Settings page. Logo/favicon are uploaded to R2; their public URL is then
 * saved alongside the other fields. `wpfeedback_*` naming lives only in the backend.
 */
export function WhitelabelPanel({ slug }: { slug: string }) {
  const { data, isLoading, isError, refetch } = useWhitelabelQuery(slug)
  const [updateWhitelabel, { isLoading: saving }] = useUpdateWhitelabelMutation()
  const [form, setForm] = useState<WhitelabelSettings>(EMPTY)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const set = <K extends keyof WhitelabelSettings>(key: K, value: WhitelabelSettings[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  if (isError) return <ErrorState onRetry={refetch} />
  if (isLoading) return <div className="h-72 animate-pulse rounded-xl bg-muted" />

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await updateWhitelabel({ slug, patch: form }).unwrap()
      toast.success('Branding saved')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <form onSubmit={save} className="max-w-xl space-y-6 rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <ImageField
          label="Logo"
          kind="logo"
          slug={slug}
          value={form.logo}
          onChange={(url) => set('logo', url)}
        />
        <ImageField
          label="Favicon"
          kind="favicon"
          slug={slug}
          value={form.favicon}
          onChange={(url) => set('favicon', url)}
        />
      </div>

      <div>
        <Label htmlFor="wl-color">Brand colour</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label="Brand colour picker"
            value={form.color || '#000000'}
            onChange={(e) => set('color', e.target.value)}
            className="h-9 w-10 shrink-0 cursor-pointer rounded-lg border border-border bg-background p-1"
          />
          <Input
            id="wl-color"
            placeholder="#6D5DF3"
            value={form.color ?? ''}
            onChange={(e) => set('color', e.target.value || null)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="wl-tutorial">Tutorial video URL</Label>
        <Input
          id="wl-tutorial"
          type="url"
          placeholder="https://…"
          value={form.tutorialVideo ?? ''}
          onChange={(e) => set('tutorialVideo', e.target.value || null)}
        />
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
        <div>
          <div className="text-sm font-medium">Show “Powered by” attribution</div>
          <p className="mt-0.5 text-xs text-muted-foreground">Display a powered-by link in the white-labelled UI.</p>
        </div>
        <Switch
          checked={form.poweredBy}
          aria-label="Show powered-by attribution"
          onCheckedChange={(checked) => set('poweredBy', checked)}
        />
      </div>

      <div>
        <Label htmlFor="wl-powered-link">“Powered by” link</Label>
        <Input
          id="wl-powered-link"
          type="url"
          placeholder="https://your-brand.example"
          value={form.poweredLink ?? ''}
          onChange={(e) => set('poweredLink', e.target.value || null)}
          disabled={!form.poweredBy}
        />
      </div>

      <div className="space-y-4 border-t border-border pt-5">
        <div>
          <div className="text-sm font-medium">Emails to SMB owners</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Atarim sends these on your behalf (plan, budget and monthly report emails) with your branding.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="wl-sender">Sender name</Label>
            <Input
              id="wl-sender"
              placeholder="Acme Hosting"
              maxLength={80}
              value={form.senderName ?? ''}
              onChange={(e) => set('senderName', e.target.value || null)}
            />
          </div>
          <div>
            <Label htmlFor="wl-reply-to">Reply-to address</Label>
            <Input
              id="wl-reply-to"
              type="email"
              placeholder="support@acmehosting.com"
              value={form.replyTo ?? ''}
              onChange={(e) => set('replyTo', e.target.value || null)}
            />
          </div>
        </div>
      </div>

      <FieldError>{error}</FieldError>
      <Button type="submit" loading={saving}>
        Save branding
      </Button>
    </form>
  )
}

function ImageField({
  label,
  kind,
  slug,
  value,
  onChange,
}: {
  label: string
  kind: 'logo' | 'favicon'
  slug: string
  value: string | null
  onChange: (url: string) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [upload, { isLoading: uploading }] = useUploadWhitelabelAssetMutation()

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (!file) return
    try {
      const res = await upload({ slug, kind, file }).unwrap()
      onChange(res.url)
      toast.success(`${label} uploaded — remember to save`)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/40">
          {value ? (
            <img src={value} alt={`${label} preview`} className="size-full object-contain" />
          ) : (
            <ImageIcon className="size-5 text-muted-foreground" aria-hidden />
          )}
        </div>
        <div className="min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp,image/x-icon"
            className="hidden"
            onChange={onFile}
          />
          <Button type="button" variant="secondary" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()}>
            {uploading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <UploadCloud className="size-4" aria-hidden />}
            {value ? 'Replace' : 'Upload'}
          </Button>
          <p className="mt-1 text-xs text-muted-foreground">PNG, JPEG, SVG, WebP or ICO · max 1 MB</p>
        </div>
      </div>
    </div>
  )
}
